import React, { useEffect, useState } from 'react';

import { ContentState, convertToRaw, EditorState } from 'draft-js';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import FormHelperText from '@mui/material/FormHelperText';
import useCookie from '../../_hook/useCookie';
import { COOKIE_KEY } from '../../constant/cookie';
import Wysiwyg, { draftToHtml, htmlToDraft } from './_module/Wysiwyg';
import InputTranslationLanguageSelect from './_module/InputTranslationLanguageSelect';
import { ITempUploadSchema } from '../../../../graphql/type/tempUpload';

interface IProps {
  values: {
    la: string;
    text: string;
  }[];
  onChange: (
    values: {
      la: string;
      text: string;
    }[]
  ) => void;
  enableUploadImage?: boolean;
  label?: string;
  info?: string;
  onUploadedImage?: (fileObj: ITempUploadSchema[]) => void;
}

export default function InputTranslationRichtext(props: IProps) {
  const {
    info,
    values,
    onChange: propOnChange,
    enableUploadImage,
    label,
    onUploadedImage,
  } = props;
  const { cookies } = useCookie([COOKIE_KEY.LANGUAGE]);
  const [editorState, setEditorState] = useState<any>(
    EditorState.createEmpty()
  );
  const [value, setValue] = useState<any>([]);
  const [language, setLanguage] = useState<any>(cookies[COOKIE_KEY.LANGUAGE]);
  const [uploadedImage, setUploadedImage] = useState<ITempUploadSchema[]>([]);

  useEffect(() => {
    if (language || value !== values) {
      let currentValue;

      if (!values) {
        currentValue = {
          la: language,
          text: '',
        };
      } else {
        currentValue = values.find((propValue) => propValue.la === language);
        currentValue = currentValue || {
          la: language,
          text: '',
        };
      }
      setValue(values);
      const contentBlock = htmlToDraft(currentValue.text);

      setEditorState(
        EditorState.createWithContent(
          ContentState.createFromBlockArray(contentBlock.contentBlocks)
        )
      );
    }
  }, [value, language]);

  function onChange(newValue: string) {
    let newValues = [...values];

    if (values && values.find((propValue) => propValue.la === language)) {
      newValues = newValues.map((item) => {
        if (item.la === language) {
          item.text = newValue;
        }
        return item;
      });
    } else {
      newValues.push({
        la: language,
        text: newValue,
      });
    }

    if (propOnChange) {
      propOnChange(newValues);
    }
  }

  return (
    <Grid container spacing={1}>
      <Grid xs={12} item>
        <FormLabel>{label}</FormLabel>
        {info && <FormHelperText>{info}</FormHelperText>}
      </Grid>
      <Grid xs={12} item>
        <FormControl variant="outlined" fullWidth size="small">
          <InputTranslationLanguageSelect
            language={language}
            type="textarea"
            variant="outlined"
            onChange={setLanguage}
          />
        </FormControl>
        <Wysiwyg
          editorState={editorState}
          enableUploadImage={enableUploadImage}
          onEditorStateChange={(newEditorState) => {
            const currentBlockKey = newEditorState.getSelection().getStartKey();
            const currentBlockIndex = newEditorState
              .getCurrentContent()
              .getBlockMap()
              .keySeq()
              .findIndex((k) => k === currentBlockKey);
            if (currentBlockIndex >= 0) {
              setEditorState(newEditorState);
              const raw = convertToRaw(newEditorState.getCurrentContent());
              onChange(draftToHtml(raw));
            }
          }}
          onUploadedImage={(tempUploads) => {
            const tempUploadedImage = [...uploadedImage, ...tempUploads];

            setUploadedImage(tempUploadedImage);
            if (onUploadedImage) {
              onUploadedImage(tempUploadedImage);
            }
          }}
        />
      </Grid>
    </Grid>
  );
}
